import { Button } from "@/components/ui/button"
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { exactCurrencyFormatter } from "@/utils"
import { Check, ChevronsUpDown, X } from "lucide-react"
import { useEffect, useMemo, useRef, useState } from "preact/hooks"
import { AccountingCheckJson, listAccountingChecks } from "@/api/accounting.tsx"
import { useOrgSettings } from "@/org_settings/state.tsx"
import { format, parseISO } from "date-fns"
import {
  AccountingCheckMatchHints,
  find_best_accounting_check_match,
  sort_accounting_checks_for_picker,
  split_check_number_row_display,
} from "@/accounting/accounting_check_match.ts"

const INTEGRATION_LABELS: Record<string, string> = {
  sageintacct: "Sage Intacct",
  quickbooks: "QuickBooks",
  xero: "Xero",
}

function getIntegrationLabel(integration: string | null): string {
  if (!integration) return "Accounting"
  return INTEGRATION_LABELS[integration.toLowerCase()] || integration
}

interface SelectedCheckSummaryProps {
  check: AccountingCheckJson
}

function SelectedCheckSummary({ check }: SelectedCheckSummaryProps) {
  const d = split_check_number_row_display(check.check_number)
  return (
    <div class="flex min-w-0 items-baseline gap-1.5 text-left">
      {d.show_check_label && <span class="text-[10px] font-medium uppercase text-slate-400">Check</span>}
      <span class="font-mono text-sm font-medium tabular-nums truncate">{d.digits}</span>
    </div>
  )
}

interface AccountingCheckAutocompleteProps {
  checkId?: string
  selectedCheckId?: string
  onSelectCheck: (check: AccountingCheckJson | null) => void
  /** When set, the list highlights matching numbers and auto-selects the best QuickBooks row once data loads. */
  matchHints?: AccountingCheckMatchHints
}

export function AccountingCheckAutocomplete({
  checkId,
  selectedCheckId,
  onSelectCheck,
  matchHints,
}: AccountingCheckAutocompleteProps) {
  const orgSettings = useOrgSettings()
  const [open, setOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const [allChecks, setAllChecks] = useState<AccountingCheckJson[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [hasLoaded, setHasLoaded] = useState(false)
  const commandListRef = useRef<HTMLDivElement>(null)
  const auto_matched_ref = useRef(false)
  const match_hints_key_ref = useRef("")

  async function loadChecks() {
    setIsLoading(true)
    const result = await listAccountingChecks()
    if (result.ok) {
      setAllChecks(result.value.data)
    }
    setIsLoading(false)
    setHasLoaded(true)
  }

  // Load checks on mount (and when org enables integration) so auto-match runs without opening the picker
  useEffect(() => {
    if (!orgSettings.accounting_integration || hasLoaded) {
      return
    }
    void loadChecks()
  }, [orgSettings.accounting_integration, hasLoaded])

  const match_hints_key = useMemo(() => {
    return `${matchHints?.checkNumber ?? ""}|${matchHints?.amountDollars ?? ""}|${matchHints?.checkDateIso ?? ""}`
  }, [matchHints?.checkNumber, matchHints?.amountDollars, matchHints?.checkDateIso])

  useEffect(() => {
    if (match_hints_key !== match_hints_key_ref.current) {
      match_hints_key_ref.current = match_hints_key
      auto_matched_ref.current = false
    }
  }, [match_hints_key])

  useEffect(() => {
    if (!hasLoaded || auto_matched_ref.current || selectedCheckId) {
      return
    }
    if (!matchHints?.checkNumber?.trim()) {
      return
    }
    auto_matched_ref.current = true
    const best = find_best_accounting_check_match(matchHints, allChecks, checkId)
    if (best) {
      onSelectCheck(best)
    }
  }, [hasLoaded, allChecks, matchHints, checkId, selectedCheckId, onSelectCheck])

  // Find the currently selected check
  const currentSelectedCheck = useMemo(() => {
    return allChecks.find((chk) => chk.id === selectedCheckId)
  }, [allChecks, selectedCheckId])

  // Filter checks based on search query (client-side filtering)
  const filteredChecks = useMemo(() => {
    let list: AccountingCheckJson[]
    if (!searchQuery.trim()) {
      list = sort_accounting_checks_for_picker(allChecks, matchHints, checkId)
    } else {
      const query = searchQuery.toLowerCase()
      list = allChecks.filter((check) => {
        return (
          check.check_number.toLowerCase().includes(query) ||
          (check.check_date && check.check_date.includes(query)) ||
          (check.customer_name && check.customer_name.toLowerCase().includes(query))
        )
      })
      list = sort_accounting_checks_for_picker(list, matchHints, checkId)
    }
    return list
  }, [allChecks, searchQuery, matchHints, checkId])

  if (!orgSettings.accounting_integration) {
    return null
  }

  const integrationLabel = getIntegrationLabel(orgSettings.accounting_integration)

  function handleSelectCheck(check: AccountingCheckJson) {
    onSelectCheck(check)
    setTimeout(() => {
      setOpen(false)
    }, 0)
  }

  function handleClear() {
    onSelectCheck(null)
  }

  function handleSearchChange(value: string) {
    setSearchQuery(value)
    // Reset scroll position when search input changes
    if (commandListRef.current) {
      commandListRef.current.scrollTop = 0
    }
  }

  function formatCheckDate(dateStr?: string): string {
    if (!dateStr) return ""
    try {
      return format(parseISO(dateStr), "MM/dd/yyyy")
    } catch {
      return dateStr
    }
  }

  return (
    <div class="flex items-center gap-2">
      <Popover open={open} onOpenChange={setOpen} modal={true}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={`flex-1 justify-start text-left font-normal ${
              currentSelectedCheck ? "bg-white border-blue-200" : "text-gray-700"
            }`}>
            <div class="flex items-center gap-2 min-w-0">
              {currentSelectedCheck ? (
                <>
                  <Check className="h-4 w-4 shrink-0 text-green-600" />
                  <SelectedCheckSummary check={currentSelectedCheck} />
                  <span class="text-sm text-gray-500 shrink-0">
                    {formatCheckDate(currentSelectedCheck.check_date)}
                  </span>
                </>
              ) : (
                <>
                  <ChevronsUpDown className="h-4 w-4 shrink-0" />
                  <span class="text-gray-500">Select {integrationLabel} Check</span>
                </>
              )}
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[min(28rem,calc(100vw-2rem))] p-0 overflow-hidden" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder={`Search ${integrationLabel} checks...`}
              value={searchQuery}
              onValueChange={handleSearchChange}
            />
            <CommandList ref={commandListRef} class="max-h-[min(24rem,50vh)]">
              {isLoading ? (
                <div class="py-6 text-center text-sm text-gray-500">Loading...</div>
              ) : (
                <>
                  <CommandEmpty>No checks found.</CommandEmpty>
                  <CommandGroup class="p-1.5">
                    {filteredChecks.map((check) => {
                      const isCurrentlySelected = check.id === selectedCheckId
                      const isLinkedElsewhere = check.linked_check_id && check.linked_check_id !== checkId
                      const row_display = split_check_number_row_display(check.check_number)

                      return (
                        <CommandItem
                          key={check.id}
                          value={check.id}
                          disabled={!!isLinkedElsewhere}
                          className={`rounded-md border border-transparent mb-0.5 px-3 py-2.5 data-[selected=true]:bg-slate-100 ${
                            isCurrentlySelected ? "!bg-sky-50 !border-sky-200" : ""
                          } ${isLinkedElsewhere ? "opacity-50" : ""}`}
                          onSelect={() => !isLinkedElsewhere && handleSelectCheck(check)}>
                          <div class="flex w-full min-w-0 flex-col gap-2">
                            <div class="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
                              <div class="flex min-w-0 items-baseline gap-2">
                                <span class="text-xs font-medium text-slate-500 shrink-0">Check #</span>
                                <span class="font-mono text-sm font-semibold tabular-nums tracking-tight text-slate-900">
                                  {row_display.digits}
                                </span>
                              </div>
                              <div class="flex shrink-0 items-baseline gap-2">
                                <span class="text-xs font-medium text-slate-500">Amount</span>
                                <span class="text-sm font-medium tabular-nums text-slate-800">
                                  {exactCurrencyFormatter(check.total_amount)}
                                </span>
                              </div>
                            </div>
                            <div class="flex flex-wrap items-start gap-x-4 gap-y-1.5 text-xs">
                              <div class="flex min-w-[8rem] max-w-full items-baseline gap-2">
                                <span class="font-medium text-slate-500 shrink-0">Date</span>
                                <span class="text-slate-700">
                                  {check.check_date ? formatCheckDate(check.check_date) : "—"}
                                </span>
                              </div>
                              <div class="flex min-w-0 flex-1 basis-[12rem] items-baseline gap-2">
                                <span class="font-medium text-slate-500 shrink-0">Customer</span>
                                <span class="min-w-0 truncate text-slate-700">
                                  {check.customer_name ?? "—"}
                                </span>
                              </div>
                              {check.payment_method?.trim() ? (
                                <span class="rounded-md border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-600">
                                  {check.payment_method.trim()}
                                </span>
                              ) : null}
                              {isLinkedElsewhere ? (
                                <span class="text-amber-800 shrink-0">Linked elsewhere</span>
                              ) : null}
                            </div>
                          </div>
                        </CommandItem>
                      )
                    })}
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {currentSelectedCheck && (
        <Button variant="ghost" size="icon" class="h-8 w-8 shrink-0" onClick={handleClear} title="Clear selection">
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  )
}
